import { Select, Chip, Typography, MenuItem } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { generatePath, useNavigate } from "react-router-dom";
import { routes } from "src/shared/constants/routes.constants";
import { Can } from "src/shared/lib/rback/Can";
import {
  contractStateColors,
  originalAvailabilityColors,
} from "../constants/colors";
import { contractLabels } from "../constants/common";
import {
  IContractAdditionalFields,
  IPartnerComplexContractItem,
} from "../types";

export function useGetTableColumns() {
  const navigate = useNavigate();

  const tableColumns: GridColDef<
    IPartnerComplexContractItem & IContractAdditionalFields
  >[] = [
    {
      headerName: "№",
      type: "string",
      field: "id",
      maxWidth: 50,
      width: 50,
      rowSpanValueGetter: (value, row) => {
        if (row.type === "virtual") {
          return null;
        }
        if (row.type === "agreement") {
          return row.contract_id;
        }

        return row.id;
      },
    },
    {
      headerName: "Договор",
      sortable: false,
      type: "string",
      field: "type",
      maxWidth: 185,
      width: 185,
      cellClassName: "vertical-align-center",
      rowSpanValueGetter: () => null,
      renderCell: ({ row }) => {
        const { type } = row;

        return (
          <>
            {type === "agreement" && (
              <Typography
                sx={{ mr: 1 }}
              >{`${row.contract_id}.${row.id}`}</Typography>
            )}
            {type && (
              <Chip
                size="small"
                label={contractLabels[type]}
                sx={{
                  height: "20px",
                  backgroundColor: (theme) =>
                    theme.palette.badges.contract[type],
                  color: (theme) => theme.palette.common.white,
                }}
              />
            )}
          </>
        );
      },
    },
    {
      headerName: "Сайт",
      type: "string",
      field: "site",
      maxWidth: 200,
      width: 150,
      rowSpanValueGetter: () => null,
    },
    {
      sortable: false,
      headerName: "",
      field: "additional",
      maxWidth: 230,
      width: 210,
      renderCell: ({ row }) => {
        if (row.type !== "complex") return null;

        return (
          <Can perform="partners.contract.edit">
            <Select
              size="small"
              displayEmpty
              value=""
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                minWidth: 180,
              }}
              renderValue={() => "+ Доп. соглашение"}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();

                if (e.target.value === "empty-template") {
                  navigate(
                    generatePath(routes.partnerComplexContractAgreementCreate, {
                      contract_id: row.id,
                      partner_id: row.partner_id,
                    }),
                  );
                }

                if (e.target.value === "contract-template-update") {
                  navigate(
                    `${generatePath(
                      routes.partnerComplexContractAgreementCreate,
                      {
                        partner_id: row.partner_id,
                        contract_id: row.id,
                      },
                    )}?template=contract`,
                  );
                }
              }}
            >
              <MenuItem value="empty-template">Пустой шаблон</MenuItem>

              <MenuItem value="contract-template-update">
                Обновление шаблона договора
              </MenuItem>
            </Select>
          </Can>
        );
      },
      rowSpanValueGetter: () => null,
    },
    {
      headerName: "№ догорвора",
      type: "string",
      field: "contract_number",
      maxWidth: 200,
      width: 200,
      cellClassName: "vertical-align-center",
      renderCell: (params) => {
        return (
          <Typography sx={{ fontWeight: 500 }}>
            {params.row.contract_number}
          </Typography>
        );
      },
      rowSpanValueGetter: () => null,
    },
    {
      headerName: "Создатель договoра",
      sortable: false,
      type: "string",
      field: "creator",
      maxWidth: 200,
      width: 200,
      valueGetter: (creator: IPartnerComplexContractItem["creator"]) =>
        creator ? `${creator?.user_lastname} ${creator?.user_firstname}` : "",
      rowSpanValueGetter: () => null,
    },
    {
      headerName: "Аккаунт менеджер",
      sortable: false,
      type: "string",
      field: "account_manager",
      maxWidth: 200,
      width: 200,
      valueGetter: (
        account_manager: IPartnerComplexContractItem["account_manager"],
      ) =>
        account_manager
          ? `${account_manager?.user_lastname} ${account_manager?.user_firstname}`
          : "",
      rowSpanValueGetter: () => null,
    },
    {
      headerName: "Статус",
      type: "custom",
      field: "contract_state",
      maxWidth: 200,
      width: 150,
      sortable: false,
      cellClassName: "vertical-align-center",
      renderCell: ({ row }) => {
        return (
          <Chip
            size="small"
            color={contractStateColors[row.contract_state.value]}
            label={row.contract_state.name}
            sx={{
              height: "20px",
            }}
          />
        );
      },
      rowSpanValueGetter: () => null,
    },

    {
      headerName: "Оригинал",
      sortable: false,
      field: "original_availability",
      flex: 1,
      cellClassName: "vertical-align-center",
      renderCell: ({ row }) => {
        return row.type == "virtual" ? null : (
          <Chip
            size="small"
            color={originalAvailabilityColors[row.original_availability.value]}
            label={row.original_availability.name}
            sx={{
              height: "20px",
            }}
          />
        );
      },
      rowSpanValueGetter: () => null,
    },
  ];

  return { tableColumns };
}
